import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, useLocation } from 'react-router'
import { useEffect } from 'react'
import { useThemeStore } from './stores/theme'
import { AuthProvider } from './contexts/AuthContext'
import { useAuth } from './hooks/useAuth'
import { AppRoutes } from './routes'
import { ThemeProvider } from './components/providers/ThemeProvider'
import { HighContrastProvider } from './components/accessibility/HighContrast'
import { ToastProvider } from './contexts/ToastContext'
import { NavigationProvider } from './contexts/NavigationContext'
import { useLogger } from './utils/logger'
import { PerformanceWidget } from './components/performance/PerformanceMonitor'
import { measureWebVitals } from './utils/performance'
import './i18n/i18n' // Initialize i18n
import './utils/autoAuth' // Initialize auto-authentication for development
import { VersionChecker } from './components/shared/VersionChecker';
import { ErrorBoundary } from './components/error/ErrorBoundary';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
})

import { usePageViewTracking } from './hooks/useAnalyticsTracking'

function ThemeRouter() {
  const themeType = useThemeStore((s) => s.themeType)
  const location = useLocation()
  const logger = useLogger('ThemeRouter')

  // Track page views automatically
  usePageViewTracking()

  useEffect(() => {
    logger.info('MSB Digital Therapy Tool initialized', {
      theme: themeType,
      metadata: {
        environment: import.meta.env.VITE_APP_ENVIRONMENT || import.meta.env.MODE,
        pathname: location.pathname
      },
    })
  }, [logger, themeType, location.pathname])

  // Initialize performance monitoring once on mount
  useEffect(() => {
    measureWebVitals()
  }, [])

  return (
    <ThemeProvider themeType={themeType}>
      <NavigationProvider>
        <ToastProvider>
          <div className={`theme-${themeType} h-full`}>
            <AppRoutes />
            <PerformanceWidget />
          </div>
        </ToastProvider>
      </NavigationProvider>
    </ThemeProvider>
  )
}

// AppContent that waits for auth to initialize
function AppContent() {
  const { loading } = useAuth();

  // Show loading screen while auth is initializing
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading Authentication...
          </p>
        </div>
      </div>
    );
  }

  return <ThemeRouter />;
}

function App() {
  return (
    <ErrorBoundary isolate>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <VersionChecker />
            <HighContrastProvider>
              <AppContent />
            </HighContrastProvider>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App